import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const TABS = [
  { id: "", label: "All" },
  { id: "travel", label: "Travel" },
  { id: "finance", label: "Finance" },
  { id: "recharge", label: "Recharge" },
  { id: "food-delivery", label: "Food delivery" },
  { id: "education", label: "Education" },
  { id: "services", label: "Services" },
  { id: "other", label: "Other" },
];

const GRID = "grid grid-cols-1 gap-5 min-[561px]:grid-cols-2 min-[901px]:grid-cols-3 min-[1201px]:grid-cols-4";

function offerLabel(text) {
  const value = String(text || "").trim();
  if (value.startsWith("Shop ") && value.endsWith(" deals")) return "Exclusive offers";
  return value;
}

function brandInitials(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "•";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words.slice(0, 2).map((word) => word[0]).join("").toUpperCase();
}

function focusRing(extra = "") {
  return `focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff2d87] ${extra}`;
}

function LogoTile({ name, logoUrl }) {
  const [failed, setFailed] = useState(false);
  if (!logoUrl || failed) {
    return (
      <div
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[12px] bg-gradient-to-br from-[#ff2d87] to-[#7c3aed] text-sm font-semibold text-white"
        aria-hidden="true"
        data-testid="deal-logo-fallback"
      >
        {brandInitials(name)}
      </div>
    );
  }
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[12px] bg-[#1f1f25]">
      <img
        src={logoUrl}
        alt={name}
        width={36}
        height={36}
        onError={() => setFailed(true)}
        className="max-h-9 max-w-9 object-contain"
        style={{ imageRendering: "auto" }}
      />
    </div>
  );
}

function DealCard({ deal }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(deal.coupon_code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article
      className="flex h-full flex-col rounded-[16px] border border-[#26262c] bg-[#141418] p-5 transition duration-200 hover:-translate-y-0.5 hover:border-[#ff2d87]/40 hover:shadow-[0_12px_32px_rgba(0,0,0,0.35)]"
      data-testid={`deal-card-${deal.slug}`}
    >
      <div className="flex items-center gap-3">
        <LogoTile name={deal.name} logoUrl={deal.logo_url} />
        <div className="min-w-0">
          <h2 className="truncate text-[16px] font-semibold leading-5 text-white">{deal.name}</h2>
          {deal.category_label ? (
            <span className="mt-1 inline-flex rounded-full bg-[#1f1f25] px-2 py-0.5 text-[11px] text-[#9a9aa3]">
              {deal.category_label}
            </span>
          ) : null}
        </div>
      </div>
      <p className="mt-4 text-[14px] font-medium leading-5 text-[#ff2d87]">{offerLabel(deal.offer_text)}</p>
      <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-[13px] leading-5 text-[#9a9aa3]">
        {deal.description || ""}
      </p>
      <div className="mt-auto pt-4">
        {deal.coupon_code ? (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-[10px] border border-dashed border-[#3a3a42] px-3 py-2">
            <span className="truncate font-mono text-sm tracking-wide text-white">{deal.coupon_code}</span>
            <button
              type="button"
              onClick={copy}
              className={focusRing("shrink-0 rounded-md px-2 py-1 text-xs font-medium text-[#ff2d87] hover:text-white")}
              data-testid={`deal-copy-${deal.slug}`}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        ) : null}
        <a
          href={deal.buy_url}
          target="_blank"
          rel="nofollow sponsored noopener"
          className={focusRing("inline-flex h-10 w-full items-center justify-center rounded-[10px] bg-[#ff2d87] text-sm font-medium text-white transition hover:bg-[#d4126e]")}
          data-testid={`deal-get-${deal.slug}`}
        >
          Get deal →
        </a>
      </div>
    </article>
  );
}

function DealSkeleton() {
  return (
    <div className="flex h-[280px] flex-col rounded-[16px] border border-[#26262c] bg-[#141418] p-5" aria-hidden="true">
      <div className="flex items-center gap-3">
        <div className="h-14 w-14 animate-pulse rounded-[12px] bg-[#1f1f25]" />
        <div className="flex-1">
          <div className="h-4 w-2/3 animate-pulse rounded bg-[#1f1f25]" />
          <div className="mt-2 h-3 w-14 animate-pulse rounded-full bg-[#1f1f25]" />
        </div>
      </div>
      <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-[#1f1f25]" />
      <div className="mt-3 h-3 w-full animate-pulse rounded bg-[#1f1f25]" />
      <div className="mt-2 h-3 w-4/5 animate-pulse rounded bg-[#1f1f25]" />
      <div className="mt-auto h-10 animate-pulse rounded-[10px] bg-[#1f1f25]" />
    </div>
  );
}

export default function Deals() {
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["deals", category, search],
    queryFn: async () => (await api.get("/deals", { params: { category, q: search, limit: 60 } })).data,
  });
  const deals = data?.deals || [];
  const total = Number(data?.total || 0);
  const countLabel = `${total} ${total === 1 ? "deal" : "deals"}`;

  return (
    <div className="mx-auto max-w-[1440px] px-4 pb-24 pt-24 sm:px-6 lg:px-10" data-testid="offers-deals">
      <h1 className="font-serif text-3xl tracking-tight text-white">Offers & Deals</h1>
      <p className="mt-2 max-w-2xl text-sm text-[#9a9aa3]" data-testid="deals-disclosure">
        We may earn a commission when you use these links.
      </p>
      <form
        className="mt-6 flex flex-col gap-3 sm:flex-row"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(q.trim());
        }}
      >
        <input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="Search brands or offers"
          aria-label="Search deals"
          className={focusRing("w-full rounded-[10px] border border-[#26262c] bg-[#141418] px-3 py-2 text-sm text-white sm:max-w-sm")}
          data-testid="deals-search"
        />
        <button type="submit" className={focusRing("rounded-[10px] border border-[#26262c] px-4 py-2 text-sm text-white hover:border-[#ff2d87]/40")}>
          Search
        </button>
      </form>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Deal categories" data-testid="deals-categories">
          {TABS.map((tab) => {
            const selected = category === tab.id;
            return (
              <button
                key={tab.id || "all"}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setCategory(tab.id)}
                className={focusRing(`rounded-full border px-3 py-1 text-xs ${selected ? "border-transparent bg-[#ff2d87] text-white" : "border-[#26262c] text-[#9a9aa3] hover:text-white"}`)}
                data-testid={`deals-tab-${tab.id || "all"}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {!isLoading ? (
          <p className="text-sm text-[#9a9aa3]" data-testid="deals-count">{countLabel}</p>
        ) : null}
      </div>
      {isLoading ? (
        <div className={`mt-8 ${GRID}`} data-testid="deals-loading" aria-busy="true" aria-live="polite">
          {Array.from({ length: 8 }).map((_, index) => <DealSkeleton key={index} />)}
        </div>
      ) : (
        <div className={`mt-8 ${GRID}`}>
          {deals.map((deal) => <DealCard key={deal.id || deal.slug} deal={deal} />)}
        </div>
      )}
      {!isLoading && deals.length === 0 ? (
        <p className="py-16 text-center text-[#9a9aa3]" data-testid="deals-empty">No deals in this category yet</p>
      ) : null}
    </div>
  );
}
